import type { ThreatOverlayAdapter, ThreatOverlayPoint } from './index';
import { useGraphStore } from '@/store/graphStore';

// Response shape from GET /api/v1/darkapi/:target
interface DarkAPIResponse {
  target: string;
  result: {
    target: string;
    score: number;
    threat_types: string[];
    first_seen: string;
    last_seen: string;
    confidence: number;
    tags: string[];
    is_known_bad: boolean;
  } | null;
}

function scoreThreatLevel(score: number): string {
  if (score >= 80) return 'critical';
  if (score >= 60) return 'high';
  if (score >= 40) return 'medium';
  if (score > 0) return 'low';
  return 'none';
}

async function fetchFallback(target: string): Promise<ThreatOverlayPoint | null> {
  try {
    const resp = await fetch(`/api/v1/darkapi/${encodeURIComponent(target)}`);
    if (!resp.ok) return null;
    const body = (await resp.json()) as DarkAPIResponse;
    const r = body.result;
    if (!r || (r.score === 0 && !r.is_known_bad && r.threat_types.length === 0)) return null;
    return {
      target,
      score: r.score,
      feeds: r.threat_types.length > 0 ? r.threat_types : ['darkapi'],
      label: r.threat_types.length > 0
        ? `DarkAPI: ${r.threat_types.join(', ')}`
        : `DarkAPI: score ${r.score}`,
      source: 'darkapi',
      confidence: r.confidence,
      threatLevel: scoreThreatLevel(r.score),
      feedDetails: { darkapi: { tags: r.tags, last_seen: r.last_seen, first_seen: r.first_seen } },
      lastSeen: r.last_seen,
    };
  } catch (err) {
    console.warn('[DarkAPIAdapter] Fallback fetch failed for', target, err);
    return null;
  }
}

export const DarkAPIAdapter: ThreatOverlayAdapter = {
  id: 'darkapi',
  label: 'DarkAPI Threats',
  priority: 1,

  async fetch(targets: string[]): Promise<ThreatOverlayPoint[]> {
    const elements = useGraphStore.getState().elements;
    const nodes = elements.filter((el) => el.group === 'nodes');

    const points: ThreatOverlayPoint[] = [];
    const enriched = new Set<string>();

    // Strategy 1: read enrichment already present in graphStore (zero network calls)
    for (const el of nodes) {
      const label = (el.data as { label: string }).label;
      if (!targets.includes(label)) continue;
      const score = (el.data as { threatScore?: number }).threatScore ?? 0;
      const feeds = (el.data as { feeds?: string[] }).feeds ?? [];
      if (score < 1 && feeds.length === 0) continue;

      enriched.add(label);
      const raw = (el.data as { raw?: Record<string, unknown> }).raw;
      points.push({
        target: label,
        score,
        feeds,
        label: feeds.length > 0 ? feeds.join(', ') : `Score: ${score}`,
        source: 'darkapi',
        threatLevel: scoreThreatLevel(score),
        feedDetails: raw?.threat as Record<string, Record<string, unknown>> | undefined,
      });
    }

    // Strategy 2: fallback API call for unenriched targets (rate-limit: max 5)
    const unenriched = targets.filter((t) => !enriched.has(t));
    if (unenriched.length > 0 && unenriched.length <= 5) {
      const results = await Promise.allSettled(unenriched.map(fetchFallback));
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value !== null) {
          points.push(r.value);
        }
      }
    }

    return points;
  },
};
